/**
 * ============================================================================
 * PrimaryLayoutView
 * ============================================================================
 *
 * Purpose: Presentational shell for (primary) route group (MVC View).
 * Responsibility: Compose header/footer/widgets via dynamic registry.
 * ------------------------------------------------------------
 * Developer Name : Praveen Tripathi
 * Purpose        : Primary layout view with safe inline headerFooterData script
 * ------------------------------------------------------------
 */

"use client";

import { memo, useEffect } from "react";
import { ModalTypes } from "@/types/modal";
import type { AssociatedProgramInterface } from "@/components/organisms/HeaderNew";
import type { PrimaryLayoutViewModel } from "../model/primary-layout.types";
import { injectInlineScript } from "@/helpers/injectInlineScript";
import {
  EnrollNowSticky,
  FooterNew,
  HeaderNew,
  SideButtons,
  StickyWidget,
} from "./layout-dynamic";

/** Escape `<` so JSON cannot close a surrounding <script> tag. */
function toInlineJson(value: unknown): string {
  const raw =
    typeof value === "string" ? value : JSON.stringify(value ?? null);
  return raw.replace(/</g, "\\u003c");
}

interface PrimaryLayoutViewProps {
  viewModel: PrimaryLayoutViewModel;
  children: React.ReactNode;
}

function PrimaryLayoutViewBase({
  viewModel,
  children,
}: PrimaryLayoutViewProps) {
  const {
    device,
    isMobile,
    headerMenu,
    headerMenuList,
    footerData,
    headerFooterData,
    pageType,
  } = viewModel;

  const associatedPrograms =
    headerMenu.associatedPrograms as AssociatedProgramInterface[];

  const headerFooterJson = toInlineJson(headerFooterData);

  useEffect(() => {
    injectInlineScript(
      "header-footer-data",
      `window.headerFooterData = ${headerFooterJson};`,
    );
  }, [headerFooterJson]);

  return (
    <>
      <HeaderNew
        associatedPrograms={associatedPrograms}
        tickyBarPageType="home"
        isMobile={device !== "desktop"}
        headerMenuList={headerMenuList}
        popularCourses={headerMenu.PopularCourses}
        newLaunches={headerMenu.NewLaunches}
        isHomePage={true}
      />
      {!isMobile && (
        <SideButtons
          associatedPrograms={associatedPrograms}
          modalType={ModalTypes.ConnectModal}
          pageType={pageType}
        />
      )}
      {isMobile && (
        <EnrollNowSticky
          associatedPrograms={associatedPrograms}
          isHomePage={true}
          forceHomeVariant
        />
      )}
      {children}
      <StickyWidget
        associatedPrograms={associatedPrograms}
      />
      <FooterNew footerData={footerData} />
    </>
  );
}

const PrimaryLayoutView = memo(PrimaryLayoutViewBase);
PrimaryLayoutView.displayName = "PrimaryLayoutView";

export default PrimaryLayoutView;
